import React, { useState, useMemo } from 'react';
import { Modal, Field, Input, Select, Btn, Badge, SectionHeader, StatRow } from '../components/UI';
import { fmtM, fmtDate, todayStr } from '../utils';
import { showToast } from '../components/UI';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const CATEGORIAS = [
  { id: 'combustible', label: 'Combustible', color: '#B03A2E' },
  { id: 'local', label: 'Inversiones en local', color: '#1A4E76' },
  { id: 'marca', label: 'Inversión en marca', color: '#472160' },
  { id: 'operativo', label: 'Gastos operativos', color: '#7A560E' },
  { id: 'otro', label: 'Otro', color: '#A8A49C' },
];

export default function GastosVariables({ store, setItem, delItem }) {
  const { movimientos } = store;
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({});
  const [filtroMes, setFiltroMes] = useState(todayStr().slice(0, 7));
  const [filtroCategoria, setFiltroCategoria] = useState('');

  const gastos = useMemo(() =>
    Object.values(movimientos || {})
      .filter(m => m.tipo === 'gasto' && (!filtroMes || m.fecha?.slice(0, 7) === filtroMes) && (!filtroCategoria || m.categoria === filtroCategoria))
      .sort((a, b) => (b.fecha || '').localeCompare(a.fecha || '')),
    [movimientos, filtroMes, filtroCategoria]);

  // Stats by category for current month
  const statsPorCategoria = useMemo(() => {
    const mesMov = Object.values(movimientos || {}).filter(m => m.tipo === 'gasto' && m.fecha?.slice(0, 7) === filtroMes);
    return CATEGORIAS.map(cat => ({
      ...cat,
      total: mesMov.filter(m => m.categoria === cat.id).reduce((a, m) => a + (parseFloat(m.monto) || 0), 0),
    })).filter(c => c.total > 0);
  }, [movimientos, filtroMes]);

  const totalMes = gastos.reduce((a, m) => a + (parseFloat(m.monto) || 0), 0);

  function openNew() {
    setForm({ monto: '', medio: 'efectivo', fecha: todayStr(), categoria: 'operativo', desc: '' });
    setModal(true);
  }

  function save() {
    const monto = parseFloat(form.monto);
    if (!monto || !form.desc) { showToast('Ingresá monto y descripción'); return; }
    const id = 'm' + Date.now();
    const cat = CATEGORIAS.find(c => c.id === form.categoria);
    setItem('movimientos', id, {
      id, tipo: 'gasto', fecha: form.fecha,
      hora: new Date().toTimeString().slice(0, 5),
      monto, medio: form.medio, signo: '-',
      categoria: form.categoria,
      categoriaLabel: cat?.label || form.categoria,
      desc: form.desc, autor: 'panel'
    });
    showToast('Gasto registrado');
    setModal(false);
  }

  const getCatStyle = (catId) => {
    const cat = CATEGORIAS.find(c => c.id === catId);
    return cat ? { color: cat.color, background: cat.color + '20' } : { color: 'var(--tx2)', background: 'var(--surf3)' };
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--rl)', padding: '14px 16px', flex: 1, minWidth: 160 }}>
          <div style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 5 }}>Total gastos del mes</div>
          <div style={{ fontSize: 24, fontWeight: 700, fontFamily: 'Courier, monospace', color: 'var(--red)' }}>{fmtM(totalMes)}</div>
        </div>
        <Btn variant="grn" onClick={openNew}>+ Nuevo gasto</Btn>
      </div>

      {/* Chart by category */}
      {statsPorCategoria.length > 0 && (
        <div style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--rl)', padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 12 }}>Por categoría</div>
          <ResponsiveContainer width="100%" height={120}>
            <BarChart data={statsPorCategoria} layout="vertical" margin={{ left: 10, right: 20 }}>
              <XAxis type="number" tick={{ fontSize: 9, fill: 'var(--tx3)' }} tickFormatter={v => fmtM(v)} />
              <YAxis type="category" dataKey="label" tick={{ fontSize: 10, fill: 'var(--tx2)' }} width={130} />
              <Tooltip formatter={v => fmtM(v)} contentStyle={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12 }} />
              <Bar dataKey="total" radius={4}>
                {statsPorCategoria.map((cat, i) => <Cell key={i} fill={cat.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <input type="month" value={filtroMes} onChange={e => setFiltroMes(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border2)', fontSize: 12, background: 'var(--surf)', color: 'var(--tx)' }} />
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button onClick={() => setFiltroCategoria('')}
            style={{ padding: '4px 12px', borderRadius: 99, border: `1px solid ${!filtroCategoria ? 'var(--grn)' : 'var(--border2)'}`, background: !filtroCategoria ? 'var(--grn-l)' : 'var(--surf2)', color: !filtroCategoria ? 'var(--grn)' : 'var(--tx2)', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
            Todas
          </button>
          {CATEGORIAS.map(cat => (
            <button key={cat.id} onClick={() => setFiltroCategoria(cat.id === filtroCategoria ? '' : cat.id)}
              style={{ padding: '4px 12px', borderRadius: 99, border: `1px solid ${filtroCategoria === cat.id ? cat.color : 'var(--border2)'}`, background: filtroCategoria === cat.id ? cat.color + '20' : 'var(--surf2)', color: filtroCategoria === cat.id ? cat.color : 'var(--tx2)', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--rl)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: 'var(--surf2)' }}>
              {['Fecha', 'Categoría', 'Descripción', 'Medio', 'Monto', ''].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '9px 13px', fontSize: 9.5, fontWeight: 700, color: 'var(--tx3)', borderBottom: '1px solid var(--border)', textTransform: 'uppercase' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {gastos.length === 0
              ? <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: 'var(--tx3)' }}>Sin gastos en este período</td></tr>
              : gastos.map(m => {
                const catStyle = getCatStyle(m.categoria);
                return (
                  <tr key={m.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '9px 13px', fontFamily: 'Courier, monospace', fontSize: 11, color: 'var(--tx3)' }}>{fmtDate(m.fecha)}</td>
                    <td style={{ padding: '9px 13px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 700, ...catStyle }}>
                        {m.categoriaLabel || m.categoria || '—'}
                      </span>
                    </td>
                    <td style={{ padding: '9px 13px', fontWeight: 500 }}>{m.desc || '—'}</td>
                    <td style={{ padding: '9px 13px', fontSize: 11, color: 'var(--tx3)' }}>{m.medio === 'efectivo' ? 'Efectivo' : 'Transferencia'}</td>
                    <td style={{ padding: '9px 13px', fontFamily: 'Courier, monospace', fontWeight: 700, color: 'var(--red)' }}>{fmtM(m.monto)}</td>
                    <td style={{ padding: '9px 13px' }}>
                      <Btn size="sm" variant="red" onClick={() => { delItem('movimientos', m.id); showToast('Eliminado'); }}>×</Btn>
                    </td>
                  </tr>
                );
              })}
            {gastos.length > 0 && (
              <tr style={{ background: 'var(--surf2)', borderTop: '1px solid var(--border)' }}>
                <td colSpan={4} style={{ padding: '9px 13px', fontWeight: 700 }}>Total</td>
                <td style={{ padding: '9px 13px', fontFamily: 'Courier, monospace', fontWeight: 700, color: 'var(--red)' }}>{fmtM(totalMes)}</td>
                <td></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {modal && (
        <Modal title="Nuevo gasto variable" onClose={() => setModal(false)} onSave={save}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 11 }}>
            <Field label="Categoría" full>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {CATEGORIAS.map(cat => (
                  <button key={cat.id} onClick={() => setForm(f => ({ ...f, categoria: cat.id }))}
                    style={{ padding: '7px 12px', borderRadius: 8, border: `2px solid ${form.categoria === cat.id ? cat.color : 'var(--border2)'}`, background: form.categoria === cat.id ? cat.color + '20' : 'var(--surf2)', color: form.categoria === cat.id ? cat.color : 'var(--tx2)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    {cat.label}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Descripción" full>
              <Input value={form.desc} onChange={v => setForm(f => ({ ...f, desc: v }))} placeholder="Detalle del gasto..." autoFocus />
            </Field>
            <Field label="Monto">
              <Input value={form.monto} onChange={v => setForm(f => ({ ...f, monto: v }))} type="number" placeholder="0" />
            </Field>
            <Field label="Fecha">
              <Input value={form.fecha} onChange={v => setForm(f => ({ ...f, fecha: v }))} type="date" />
            </Field>
            <Field label="Medio" full>
              <div style={{ display: 'flex', gap: 8 }}>
                {[['efectivo', 'Efectivo'], ['transferencia', 'Transferencia']].map(([val, label]) => (
                  <button key={val} onClick={() => setForm(f => ({ ...f, medio: val }))}
                    style={{ flex: 1, padding: 10, borderRadius: 8, border: `2px solid ${form.medio === val ? 'var(--grn)' : 'var(--border2)'}`, background: form.medio === val ? 'var(--grn-l)' : 'var(--surf2)', color: form.medio === val ? 'var(--grn)' : 'var(--tx2)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    {label}
                  </button>
                ))}
              </div>
            </Field>
          </div>
        </Modal>
      )}
    </div>
  );
}
